import { Component, type ErrorInfo, type ReactNode } from 'react';
import { trackException } from '@/telemetry/appInsights';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level render-error boundary (ADO #14221). Reports the error to Application Insights
 * (no-op when telemetry is not configured) and shows a minimal, PII-free fallback.
 * Deliberately theme-independent: it sits outside FluentProvider so it can catch provider failures.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // componentStack can be very long; 4 KB keeps the envelope within App Insights limits.
    trackException(error, { componentStack: errorInfo.componentStack?.slice(0, 4096), source: 'ErrorBoundary' });
    this.props.onError?.(error, errorInfo);
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Something went wrong. Please try again.</p>
        <button type="button" onClick={() => window.location.reload()}>
          Reload page
        </button>
      </div>
    );
  }
}
